import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
// import dayjs from 'dayjs';
import React from 'react';
import { Controller } from 'react-hook-form';
import Button from '../common/Button';
import Card from '../common/Card';
import Dropdown from '../common/Dropdown';
import FormDatePicker from '../common/FormDatePicker';
import FormField from '../common/FormField';
import Pill from '../common/Pill';
import TextArea from '../common/TextArea';
import { SectionHeading } from '../common/styled/SectionHeading';
import { MetaOption } from '../../types';
import { useTranslation } from 'react-i18next';

interface Meta {
  managers: MetaOption[];
  vessels: MetaOption[];
  suppliers: MetaOption[];
  ports: MetaOption[];
  pricelists: MetaOption[];
}

interface RobsFormProps {
  meta?: Meta;
  typeLabel?: string;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  onCancel: () => void;
  submitting?: boolean;
  managers?: MetaOption[];
  vessels?: MetaOption[];
  availableDates?: { startDate: string; endDate: string };
  control: any;
  errors: any;
  setValues: any;
  isAdmin?: boolean;
  robType?: string;
  isEditMode?: boolean;
}

const RobsForm: React.FC<RobsFormProps> = ({
  meta: _meta,
  typeLabel,
  onSubmit,
  onCancel,
  submitting = false,
  managers,
  vessels,
  // availableDates,
  control,
  errors,
  setValues,
  isAdmin,
  robType,
  isEditMode = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      <Card
        sx={{
          padding: '24px',
          height: '100%',
          position: 'relative',
          minHeight: '100%',
        }}
      >
        <Box sx={{ mb: 1.5, mt: -1.5 }}>
          <SectionHeading>{t('robs.robInformation')}</SectionHeading>
        </Box>

        <Box
          sx={{
            height: '2px',
            backgroundColor: theme.palette.dropdown.border,
            mx: '-24px',
            mb: 3,
          }}
        />

        <form onSubmit={onSubmit as any}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 3,
            }}
          >
            {/* Left Column */}
            <Box sx={{ flex: 1 }}>
              <FormField label={t('form.type')}>
                <Pill
                  sx={{
                    maxWidth: '500px',
                    minWidth: '150px',
                    justifyContent: 'start',
                  }}
                >
                  {typeLabel || t('robs.standardRob')}
                </Pill>
              </FormField>

              {/* Manager */}
              <FormField
                label={t('form.manager')}
                error={!!errors.manager}
                helperText={errors.manager?.message || ''}
                sx={{ mt: 3.5 }}
                required
              >
                <Controller
                  name="manager"
                  control={control}
                  rules={{ required: t('form.validation.managerRequired') }}
                  defaultValue=""
                  render={({ field }) => (
                    <Dropdown
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e: any) => {
                        field.onChange(e?.target?.value ?? '');
                        setValues('vessel', '');
                        setValues('date', '');
                      }}
                      options={managers}
                      placeholder={t('form.selectManager')}
                      debounceMs={500}
                      disabled={!isAdmin || isEditMode}
                    />
                  )}
                />
              </FormField>

              {/* Vessel */}
              <FormField
                label={t('form.vessel')}
                error={!!errors.vessel}
                helperText={errors.vessel?.message || ''}
                sx={{ mt: 1 }}
                required
              >
                <Controller
                  name="vessel"
                  control={control}
                  rules={{ required: t('form.validation.vesselRequired') }}
                  defaultValue=""
                  render={({ field }) => (
                    <Dropdown
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e: any) => {
                        field.onChange(e?.target?.value ?? '');
                        setValues('date', '');
                      }}
                      options={vessels}
                      placeholder={t('form.selectVessel')}
                      debounceMs={500}
                      disabled={isEditMode}
                    />
                  )}
                />
              </FormField>

              {/* Date */}
              {robType?.toLowerCase() === 'standard' && (
                <FormField
                  label={t('form.date')}
                  error={!!errors.date}
                  helperText={errors.date?.message || ''}
                  required
                >
                  <Controller
                    name="date"
                    control={control}
                    rules={{
                      required: t('form.validation.dateRequired'),
                      validate: (value: any) => {
                        if (!value) return t('form.validation.dateRequired');
                        // const d = dayjs(value);
                        // const min = availableDates?.startDate
                        //   ? dayjs(availableDates.startDate).startOf('day')
                        //   : null;
                        // const max = availableDates?.endDate
                        //   ? dayjs(availableDates.endDate).endOf('day')
                        //   : null;
                        // if (min && d.isBefore(min))
                        //   return t('form.validation.dateAfter', { date: min.format('YYYY/MM/DD') });
                        // if (max && d.isAfter(max))
                        //   return t('form.validation.dateBefore', {
                        //     date: max.format('YYYY/MM/DD'),
                        //   });
                        // return true;
                      },
                    }}
                    defaultValue={null}
                    render={({ field }) => (
                      <FormDatePicker
                        {...field}
                        // minDate={availableDates?.startDate ? dayjs(availableDates.startDate) : undefined}
                        // maxDate={dayjs(new Date())}
                        value={field.value}
                        onChange={(date: any) => field.onChange(date)}
                        disabled={isEditMode}
                      />
                    )}
                  />
                </FormField>
              )}
            </Box>

            {/* Right Column */}
            <Box sx={{ flex: 1 }}>
              <FormField label={t('form.remarks')}>
                <Controller
                  name="remarks"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextArea
                      {...field}
                      value={field.value || ''}
                      onChange={(e: any) => field.onChange(e.target.value)}
                      placeholder={t('form.remarksPlaceholder')}
                    />
                  )}
                />
              </FormField>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              mt: 0,
              position: 'absolute',
              right: '20px',
              bottom: '20px',
            }}
          >
            <Button variant="red" onClick={onCancel}>
              {t('buttons.cancel')}
            </Button>
            <Button variant="outlinedBlue" type="submit" isLoading={submitting}>
              {t('buttons.proceed')}
            </Button>
          </Box>
        </form>
      </Card>
    </Box>
  );
};

export default React.memo(RobsForm);
